if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.selectedThumbnailMediaId = null;
        this.selectedThumbnailLockTimeout = null;

        // Native videos play inline. Remember where a video was paused so it can resume from there.
        // 'pause' doesn't bubble, so listen in the capture phase to also catch videos added later (variant change).
        this.addEventListener(
          'pause',
          (event) => {
            if (event.target.nodeName === 'VIDEO') event.target.dataset.resumeTime = event.target.currentTime;
          },
          true
        );
        this.elements.viewer.addEventListener('slideChanged', this.onSlideChangedVideo.bind(this));
        this.playVideo(this.elements.viewer.querySelector('.is-active'));

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChangedVideo(event) {
        const activeItem = event.detail.currentElement;
        // Pause videos on slides that are left right away...
        this.querySelectorAll('video').forEach((video) => {
          if (!activeItem?.contains(video)) video.pause();
        });
        // ...but wait for the slider to settle before playing, so a video isn't started while swiping past it.
        window.clearTimeout(this.playVideoTimeout);
        this.playVideoTimeout = window.setTimeout(() => this.playVideo(activeItem), 150);
      }

      playVideo(slide) {
        const video = slide?.querySelector('video');
        if (!video) return;

        // Autoplay is only allowed for muted, inline videos. Muted once, so a later unmute via the controls is kept.
        video.playsInline = true;
        video.controls = true;
        if (!video.dataset.galleryReady) {
          video.muted = true;
          video.dataset.galleryReady = 'true';
        }

        const resumeTime = parseFloat(video.dataset.resumeTime);
        if (resumeTime > 0 && Math.abs(video.currentTime - resumeTime) > 0.25) video.currentTime = resumeTime;

        const playing = video.play();
        if (!playing) return;
        playing.catch((error) => {
          // Retry muted if the browser refused to autoplay with sound. Other errors (e.g. paused mid-load) are ignored.
          if (error.name === 'NotAllowedError' && !video.muted) {
            video.muted = true;
            video.play().catch(() => {});
          }
        });
      }

      onSlideChanged(event) {
        // While a thumbnail click is scrolling the slider, keep the clicked thumbnail active instead of the passed-over slides
        const targetMediaId = this.selectedThumbnailMediaId || event.detail.currentElement.dataset.mediaId;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${targetMediaId}"]`);
        this.setActiveThumbnail(thumbnail, { userTriggered: false });
      }

      setActiveMedia(mediaId, prepend) {
        this.selectedThumbnailMediaId = mediaId;
        window.clearTimeout(this.selectedThumbnailLockTimeout);
        this.selectedThumbnailLockTimeout = window.setTimeout(() => {
          this.selectedThumbnailMediaId = null;
        }, 450);

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail, { userTriggered: true });
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail, { userTriggered = false } = {}) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (!this.elements.thumbnails.slider) return;
        const slider = this.elements.thumbnails.slider;
        const sliderItemOffset = this.elements.thumbnails.sliderItemOffset || thumbnail.clientWidth;
        const maxScrollLeft = Math.max(0, slider.scrollWidth - slider.clientWidth);
        const thumbnailLeft = thumbnail.offsetLeft;
        const thumbnailRight = thumbnailLeft + thumbnail.clientWidth;
        const viewportLeft = slider.scrollLeft;
        const viewportRight = viewportLeft + slider.clientWidth;
        const edgeThreshold = Math.min(10, Math.round(sliderItemOffset * 0.1));
        const isAtLeftEdge = thumbnailLeft <= viewportLeft + edgeThreshold;
        const isAtRightEdge = thumbnailRight >= viewportRight - edgeThreshold;
        const isFullyVisible = thumbnailLeft >= viewportLeft && thumbnailRight <= viewportRight;
        let targetLeft = null;

        // Without arrows on the strip, reveal the neighbouring thumbnail when the clicked one sits at an edge
        if (userTriggered && isAtLeftEdge && viewportLeft > 0) {
          targetLeft = Math.max(0, viewportLeft - sliderItemOffset);
        } else if (userTriggered && isAtRightEdge && viewportRight < slider.scrollWidth) {
          targetLeft = Math.min(maxScrollLeft, viewportLeft + sliderItemOffset);
        } else if (!isFullyVisible) {
          targetLeft = Math.min(Math.max(0, thumbnailLeft), maxScrollLeft);
        }

        if (targetLeft === null) return;

        this.elements.thumbnails.setSlidePosition(targetLeft);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
        this.playVideo(activeItem);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
